import { watch, type Ref } from 'vue';
import { trackEcommerceImpressions, trackEcommerceProductClick } from '@/entities/analytics';
import type { ProductSearch } from '@/entities/product';
import type { SearchCriteria } from '@/entities/searchCriteria';
import type { SerpProduct } from './types';
import { SERP_ECOMMERCE_LISTS } from '../config/ecommerce';
import {
  getSerpEcommerceItem,
  getSerpEcommerceListSignature,
  getSerpEcommercePage,
  getSerpEcommercePagesCount,
} from '../lib/serpEcommerce';

interface SerpEcommerceOptions {
  products: Readonly<Ref<SerpProduct[]>>;
  listKey: Readonly<Ref<string>>;
  visibleCount: Readonly<Ref<number>>;
  criteria: Readonly<Ref<SearchCriteria>>;
}

export interface SerpEcommerce {
  trackProductClick(serpProduct: SerpProduct): void;
}

function watchImpressions(list: string, { products, listKey, visibleCount, criteria }: SerpEcommerceOptions): void {
  let signature: string | null = null;
  let reportedPages = 0;

  const reportPage = (page: number): void => {
    trackEcommerceImpressions(getSerpEcommercePage(products.value, page, criteria.value), list);
  };

  const reportVisiblePages = (): void => {
    const pagesCount = getSerpEcommercePagesCount(Math.min(visibleCount.value, products.value.length));

    for (; reportedPages < pagesCount; reportedPages += 1) reportPage(reportedPages);
  };

  const syncImpressions = (): void => {
    const nextSignature = getSerpEcommerceListSignature(listKey.value, products.value);

    if (nextSignature === signature) {
      reportVisiblePages();
      return;
    }

    signature = nextSignature;
    reportedPages = 0;

    if (!products.value.length) return;

    reportPage(0);
    reportedPages = 1;
  };

  watch([products, listKey], syncImpressions, { immediate: true });
  watch(visibleCount, syncImpressions);
}

export function useSerpEcommerce(productSearch: ProductSearch, options: SerpEcommerceOptions): SerpEcommerce {
  const list = SERP_ECOMMERCE_LISTS[productSearch.type];

  if (list) watchImpressions(list, options);

  const trackProductClick = (serpProduct: SerpProduct): void => {
    const position = options.products.value.indexOf(serpProduct);

    if (!list || position < 0) return;

    const item = getSerpEcommerceItem(serpProduct, position, options.criteria.value);

    if (item) trackEcommerceProductClick(item, list);
  };

  return { trackProductClick };
}
